import { useState } from "react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { apiError } from "@/lib/api";
import { useMyTickets, useCreateTicket } from "@/hooks/useSupport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { LifeBuoy, MessageSquare } from "lucide-react";

export default function Support() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const { data: tickets = [] } = useMyTickets();

  const createMutation = useCreateTicket({
    onSuccess: () => {
      toast.success("Ticket submitted. We'll get back to you soon!");
      setSubject(""); setMessage("");
    },
    onError: (e) => toast.error(apiError(e)),
  });
  const loading = createMutation.isPending;

  const submit = (e) => {
    e.preventDefault();
    createMutation.mutate({ subject, message });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-12 grid md:grid-cols-2 gap-10">
        <div>
          <div className="flex items-center gap-2 mb-2"><LifeBuoy className="h-5 w-5 text-primary" /><p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">Support</p></div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mb-2">How can we help?</h1>
          <p className="text-muted-foreground mb-8 text-sm">Send us a message and our team will reply right here.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="support-form">
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Subject</Label>
              <Input required value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-none h-11" placeholder="Billing, course quality, etc." data-testid="ticket-subject-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Message</Label>
              <Textarea required value={message} onChange={(e) => setMessage(e.target.value)} className="rounded-none min-h-32" placeholder="Tell us what's going on..." data-testid="ticket-message-input" />
            </div>
            <Button type="submit" disabled={loading} className="rounded-none h-11 px-6 bg-primary hover:opacity-90" data-testid="submit-ticket-button">
              {loading ? "Sending..." : "Submit ticket"}
            </Button>
          </form>
        </div>

        <div>
          <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Your tickets</h2>
          {tickets.length === 0 ? (
            <p className="text-sm text-muted-foreground border border-dashed border-border p-6">No tickets yet.</p>
          ) : (
            <div className="space-y-4" data-testid="ticket-list">
              {tickets.map((t) => (
                <div key={t.id} className="border border-border bg-card p-4" data-testid={`ticket-${t.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-sm">{t.subject}</h3>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${t.status === "answered" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{t.status}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{t.message}</p>
                  {t.replies?.map((r, i) => (
                    <div key={i} className="mt-3 border-l-2 border-primary pl-3 text-sm">
                      <span className="text-xs font-semibold text-primary uppercase tracking-wider">Support</span>
                      <p className="text-foreground">{r.message}</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
